import React, { useEffect, useRef, useState } from 'react';
import { Grid, IconButton, styled, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';
import { AddCircleOutline, CheckCircle, Delete, RemoveCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
// moment-timezone rather than plain moment: .tz() below needs the plugin loaded, and relying on
// some other module having imported it first is a trap.
import moment from 'moment-timezone';
import { AccountData, ActionSource, OrderData, OrderService } from '../../../../../api';
import { useSafeExecute } from '../../../../../common/apiNotifications/safeExecute';
import { usePriceFormatter } from '../../../../../common/usePriceFormatter';
import { UseAuthPermissions } from '../../../../../session/UseAuthPermissions';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useUsers } from '../../../../../session/swr/useUsers';
import { getUserFullNameFromUserId } from '../../../../components/UsersMultiSelect';
import { getDateFormat, getTimeFormat } from '../../../../../common/getDateAndTimeFormat';
import { useCalendar } from '../../../../CalendarContext/CalendarContext';
import { useCatalogCategories } from '../../../../../catalog/swr/useCatalogCategories';
import { AddExtraItemDialog } from './AddExtraItemDialog';
import { ExtraPaymentDialog } from './ExtraPaymentDialog';
import { ExtraItemsQuickAction } from './ExtraItemsQuickAction';
import { celebratePayment } from './celebratePayment';

const T_PREFIX = 'calendar.edit_order.order_details.extraItems';

const StyledTableCell = styled(TableCell)({
  padding: '0 8px 0 0',
});

type ExtraItemsSectionProps = {
  orderId: string;
  order: OrderData;
  orderPageId?: string;
  onCommitted: (freshOrder: OrderData) => void;
  // Set when the dialog was opened straight from "finalise bill", to bring this section into
  // view instead of leaving staff to scroll for it.
  shouldFocus?: boolean;
  // Set by the quick actions on the collapsed panel header; a new token re-fires the same action.
  requestedAction?: { type: ExtraItemsQuickAction; token: number } | null;
};

export const ExtraItemsSection = ({
  orderId,
  order,
  orderPageId,
  onCommitted,
  shouldFocus,
  requestedAction,
}: ExtraItemsSectionProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const priceFormatter = usePriceFormatter();
  const { readOnly } = UseAuthPermissions();
  const { categories } = useCatalogCategories();
  const { users } = useUsers();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSettleOpen, setIsSettleOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const balanceBoxRef = useRef<HTMLDivElement | null>(null);
  const { editOrderDrawerState } = useCalendar();
  const shouldOpenCatalog = Boolean(editOrderDrawerState?.isOpen && editOrderDrawerState.openCatalog);
  const shouldOpenCheckout = Boolean(editOrderDrawerState?.isOpen && editOrderDrawerState.openCheckout);

  useEffect(() => {
    if (shouldFocus) {
      // The panel it lives in expands on the same render, so wait a frame for the layout.
      const timer = setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldFocus]);

  const requestedActionType = requestedAction?.type;
  const requestedActionToken = requestedAction?.token;
  useEffect(() => {
    if (requestedActionToken === undefined) {
      return undefined;
    }
    // The panel expands on the same render, so wait for the layout like the effects around it.
    const timer = setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (requestedActionType === ExtraItemsQuickAction.ADD_ITEMS) {
        setIsAddOpen(true);
      } else if (requestedActionType === ExtraItemsQuickAction.PAYMENT) {
        setIsSettleOpen(true);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [requestedActionToken, requestedActionType]);

  useEffect(() => {
    if (shouldOpenCatalog) {
      const timer = setTimeout(() => setIsAddOpen(true), 250);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldOpenCatalog]);

  useEffect(() => {
    if (shouldOpenCheckout) {
      const timer = setTimeout(() => setIsSettleOpen(true), 250);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldOpenCheckout]);

  const extraItems = order?.extra_items ?? [];
  const itemsTotal = extraItems.reduce((total, line) => total + line.unit_price * line.quantity, 0);
  // Server-computed, never derived here: the number staff read must be the number the
  // saved-card charge will take.
  const balanceDue = order?.balance_due;
  const amountDue = balanceDue !== undefined && balanceDue !== null ? Math.max(0, balanceDue) : undefined;
  const isPaidInFull = !!extraItems.length && amountDue === 0;
  const hasCatalog = categories.some((category) => !category.archived);

  // Keyed by order: the drawer can switch orders without closing, and moving from an owing order to a
  // paid one is not a payment.
  const lastBalanceRef = useRef({ orderId, amountDue });
  useEffect(() => {
    const wasOwed = lastBalanceRef.current.orderId === orderId && (lastBalanceRef.current.amountDue ?? 0) > 0;
    lastBalanceRef.current = { orderId, amountDue };
    if (wasOwed && amountDue === 0 && balanceBoxRef.current) {
      celebratePayment(balanceBoxRef.current);
    }
  }, [orderId, amountDue]);

  // Accounts that never set up a catalog get no new section at all.
  if (!hasCatalog && !extraItems.length) {
    return null;
  }

  const runLineMutation = async (apiCall: () => Promise<OrderData>, successMessage: string) => {
    setIsMutating(true);
    try {
      const freshOrder = await safeExecute({
        apiCall,
        successMessage,
        // Quantity steps are noisy enough without a toast per tap.
        suppressSuccessMessage: !successMessage,
      });
      onCommitted(freshOrder);
    } catch {
      // safeExecute already surfaced the toast.
    } finally {
      setIsMutating(false);
    }
  };

  // Who added the line and when. added_by may be a user who has since left the account, so it
  // falls back to nothing rather than showing a raw id.
  const renderAddedByAndDate = (addedBy?: string, createdAt?: string, source?: ActionSource) => {
    const addedByName =
      source === ActionSource.CUSTOMER
        ? t(`${T_PREFIX}.customerChosen`)
        : addedBy
        ? getUserFullNameFromUserId(users, addedBy)
        : '';
    // The API serialises created_at as a bare "YYYY-MM-DD HH:mm:ss.SSSSSS" with no offset, and it
    // is UTC (datetime.utcnow). Parsed plainly, moment would read it as browser-local and show
    // the clock hours behind, so it is read as UTC and moved into the account's timezone.
    const addedAt = createdAt
      ? moment
          .utc(createdAt)
          .tz(account.timezone)
          .format(`${getDateFormat(account).shortDateFormat} ${getTimeFormat(account)}`)
      : '';
    const parts = [addedByName, addedAt].filter(Boolean);
    if (!parts.length) {
      return null;
    }
    return (
      <Typography variant="caption" color="text.secondary">
        {parts.join(' · ')}
      </Typography>
    );
  };

  const changeQuantity = (lineId: string, quantity: number) =>
    runLineMutation(() => OrderService.updateOrderExtraItem({ order_id: orderId, item_line_id: lineId, quantity }), '');

  return (
    <Grid item container flexDirection="column" width="100%" mt={2} ref={sectionRef}>
      <Grid item container justifyContent="space-between" alignItems="center">
        <Typography fontWeight="700">{t(`${T_PREFIX}.title`)}</Typography>
        {!readOnly && (
          <Button onClick={() => setIsAddOpen(true)} variant="outlined" style={{ textTransform: 'none' }}>
            {t(`${T_PREFIX}.addItem`)}
            <Add />
          </Button>
        )}
      </Grid>

      {!extraItems.length ? (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {t(`${T_PREFIX}.empty`)}
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {extraItems.map((line) => (
              <TableRow key={line.id}>
                <StyledTableCell>
                  <Grid container flexDirection="column">
                    <Typography variant="body2">{line.label}</Typography>
                    {renderAddedByAndDate(line.added_by, line.created_at, line.source)}
                  </Grid>
                </StyledTableCell>
                <StyledTableCell style={{ minWidth: '110px' }}>
                  <Grid container alignItems="center" flexWrap="nowrap">
                    {!readOnly && (
                      <IconButton
                        size="small"
                        disabled={isMutating || line.quantity <= 1}
                        onClick={() => changeQuantity(line.id, line.quantity - 1)}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    )}
                    <Typography variant="body2" dir="ltr">
                      {line.quantity}
                    </Typography>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        disabled={isMutating}
                        onClick={() => changeQuantity(line.id, line.quantity + 1)}>
                        <AddCircleOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Grid>
                </StyledTableCell>
                <StyledTableCell style={{ minWidth: '70px' }}>
                  {priceFormatter(line.unit_price * line.quantity)}
                </StyledTableCell>
                <StyledTableCell>
                  {!readOnly && (
                    <Tooltip title={t(`${T_PREFIX}.removeItem`)} arrow>
                      <IconButton
                        size="small"
                        disabled={isMutating}
                        onClick={() =>
                          runLineMutation(
                            () => OrderService.removeOrderExtraItem(orderId, line.id),
                            t(`${T_PREFIX}.itemRemoved`),
                          )
                        }>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </StyledTableCell>
              </TableRow>
            ))}
            <TableRow>
              <StyledTableCell>
                <Typography variant="body2" fontWeight="700">
                  {t(`${T_PREFIX}.itemsTotal`)}
                </Typography>
              </StyledTableCell>
              <StyledTableCell />
              <StyledTableCell style={{ minWidth: '70px' }}>{priceFormatter(itemsTotal)}</StyledTableCell>
              <StyledTableCell />
            </TableRow>
          </TableBody>
        </Table>
      )}

      {balanceDue !== undefined && balanceDue !== null && (
        <Grid
          item
          container
          ref={balanceBoxRef}
          justifyContent={isPaidInFull ? 'center' : 'space-between'}
          alignItems="center"
          gap={1}
          flexWrap="nowrap"
          mt={isPaidInFull ? 2 : 1}
          p={isPaidInFull ? 3 : 1}
          borderRadius="8px"
          // An unpaid balance is the one thing on this panel that needs chasing before the
          // customer leaves, so it carries the error colour rather than the neutral frame.
          sx={
            amountDue
              ? { border: '2px solid', borderColor: 'error.main', backgroundColor: 'error.lighter' }
              : isPaidInFull
              ? { border: '2px solid', borderColor: 'primary.main', backgroundColor: 'primary.light' }
              : { border: '1px solid gray' }
          }>
          <Grid item container width="auto" alignItems="center" gap={isPaidInFull ? 1.5 : 1} flexWrap="nowrap">
            {isPaidInFull && <CheckCircle color="primary" sx={{ fontSize: 44 }} />}
            <Typography
              fontWeight={isPaidInFull ? 800 : 700}
              fontSize={isPaidInFull ? 28 : undefined}
              color={amountDue ? 'error.main' : isPaidInFull ? 'primary.main' : undefined}>
              {balanceDue < 0
                ? t(`${T_PREFIX}.credit`)
                : isPaidInFull
                ? t(`${T_PREFIX}.paidInFull`)
                : t(`${T_PREFIX}.balanceDue`)}
            </Typography>
            {balanceDue !== 0 && priceFormatter(Math.abs(balanceDue))}
          </Grid>
          {!!amountDue && !readOnly && (
            <Button
              size="small"
              variant="contained"
              color="error"
              sx={{ textTransform: 'none', boxShadow: 'none' }}
              onClick={() => setIsSettleOpen(true)}>
              {t(`${T_PREFIX}.collectBalance`)}
            </Button>
          )}
        </Grid>
      )}

      <AddExtraItemDialog
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        orderId={orderId}
        orderPageId={orderPageId}
        existingLines={order.extra_items ?? []}
        onCommitted={onCommitted}
      />
      {isSettleOpen && (
        <ExtraPaymentDialog
          isOpen={isSettleOpen}
          onClose={() => setIsSettleOpen(false)}
          orderId={orderId}
          order={order}
          initialPrice={amountDue}
          initialTitle={t(`${T_PREFIX}.balanceDue`)}
          onCommitted={onCommitted}
        />
      )}
    </Grid>
  );
};
